import { ReactNode, useState } from 'react';
import { Button } from '../atoms';
import { useIsCompact } from '../lib/useMediaQuery';

/**
 * Wizard — generic multi-step form scaffold.
 *
 * M3 has no canonical "Wizard" component; M3 Stepper (in Material
 * Components for Android) is the closest spec. Populus composes a
 * step indicator + per-step content slot + Back/Next button row.
 * Per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/cards/overview (surface)
 *                + Stepper guidance from M3 Android docs
 *   - Reference: github.com/material-components/material-web (no
 *                first-party stepper at time of authoring)
 *
 * Populus adaptations applied:
 *   - Step indicator: numbered circles + labels + connecting line; current
 *     step in `--brand-primary`; completed in `--color-status-green`;
 *     pending in `--color-muted` (vs M3 Stepper's filled-rail approach;
 *     Populus prefers explicit step visibility for high-stakes onboarding)
 *   - Children prop receives `(currentStep, setStep, isLast, isFirst)`
 *     render-prop API so the consumer owns the per-step content rendering
 *     + validation gating
 *   - `canAdvance: () => boolean` callback lets the consumer gate Next
 *     button when current step is invalid
 *   - Back / Next / Submit button row sticky at the bottom; Submit
 *     label customizable
 *
 * Token discipline:
 *   - Surface → consumer wraps in `--color-card` if desired
 *   - Step number circle → `--brand-primary` (org-overridable) for current
 *     step; status tokens for completed/pending (system; semantic)
 *
 * Phase 11 Block 8 PR 5 (2026-05-12).
 */

export interface WizardStep {
  label: string;
  description?: string;
}

export interface WizardRenderProps {
  currentStep: number;
  setStep: (step: number) => void;
  isFirst: boolean;
  isLast: boolean;
}

export interface WizardProps {
  steps: WizardStep[];
  initialStep?: number;
  onSubmit: () => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  submitting?: boolean;
  canAdvance?: (currentStep: number) => boolean;
  children: (props: WizardRenderProps) => ReactNode;
}

export function Wizard({
  steps,
  initialStep = 0,
  onSubmit,
  onCancel,
  submitLabel = 'Submit',
  submitting = false,
  canAdvance,
  children,
}: WizardProps) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  // F3 — the horizontal stepper (nowrap labels + connecting lines) overflows the
  // viewport on a phone with 3–4 steps. Below the lg breakpoint, condense it to
  // "Step N of M · <label>" + a progress bar, which fits any width.
  const compact = useIsCompact();
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const advanceBlocked = canAdvance ? !canAdvance(currentStep) : false;

  function handleNext() {
    if (advanceBlocked) return;
    if (isLast) {
      void onSubmit();
    } else {
      setCurrentStep(s => s + 1);
    }
  }

  function handleBack() {
    if (isFirst) return;
    setCurrentStep(s => s - 1);
  }

  return (
    <div>
      {compact ? (
        <div style={{ margin: '0 0 var(--sp-5)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
            <span style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {steps[currentStep]?.label}
            </span>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', flexShrink: 0 }}>
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          {steps[currentStep]?.description && (
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)', marginBottom: 'var(--sp-2)' }}>{steps[currentStep].description}</div>
          )}
          <div style={{ height: 4, borderRadius: 999, background: 'var(--color-border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${((currentStep + 1) / steps.length) * 100}%`, background: 'var(--brand-primary)', transition: 'width .2s ease' }} />
          </div>
        </div>
      ) : (
      <ol style={{
        display: 'flex',
        gap: 'var(--sp-2)',
        listStyle: 'none',
        padding: 0,
        margin: '0 0 var(--sp-6)',
        alignItems: 'center',
      }}>
        {steps.map((step, i) => {
          const isCurrent = i === currentStep;
          const isCompleted = i < currentStep;
          const circleBg = isCurrent
            ? 'var(--brand-primary)'
            : isCompleted
              ? 'var(--color-status-green)'
              : 'var(--color-bg)';
          const circleColor = isCurrent || isCompleted ? '#fff' : 'var(--color-ink-3)';
          const circleBorder = isCurrent || isCompleted ? 'transparent' : 'var(--color-border)';
          return (
            <li key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              flex: i < steps.length - 1 ? '1 1 0' : '0 0 auto',
            }}>
              <div style={{
                width: 28, height: 28,
                borderRadius: '50%',
                background: circleBg,
                color: circleColor,
                border: `1px solid ${circleBorder}`,
                display: 'grid',
                placeItems: 'center',
                fontSize: 'var(--fs-sm)',
                fontWeight: 'var(--fw-semibold)',
                flexShrink: 0,
              }}>{isCompleted ? '✓' : i + 1}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 'var(--fw-medium)',
                  color: isCurrent ? 'var(--color-ink)' : 'var(--color-ink-3)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>{step.label}</div>
                {step.description && (
                  <div style={{
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--color-muted)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>{step.description}</div>
                )}
              </div>
              {i < steps.length - 1 && (
                <div style={{
                  flex: 1,
                  height: 1,
                  background: isCompleted ? 'var(--color-status-green)' : 'var(--color-border)',
                  margin: '0 var(--sp-2)',
                }} />
              )}
            </li>
          );
        })}
      </ol>
      )}

      <div>
        {children({ currentStep, setStep: setCurrentStep, isFirst, isLast })}
      </div>

      <footer style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        marginTop: 'var(--sp-6)',
        paddingTop: 'var(--sp-4)',
        borderTop: '1px solid var(--color-border)',
      }}>
        <div>
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleBack}
            disabled={isFirst || submitting}
          >
            Back
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleNext}
            disabled={advanceBlocked || submitting}
            loading={isLast && submitting}
          >
            {isLast ? submitLabel : 'Next'}
          </Button>
        </div>
      </footer>
    </div>
  );
}
